"use client"

/**
 * Post-result tool → grass-ID handoff card.
 *
 * 105 sessions/day enter the site on a /tools/* page and about half compute a
 * result, then dead-end: there was no cross-sell into the grass-ID flow at the
 * peak moment. This is that surface, and it renders ONLY when the tool does not
 * actually know the user's grass type (a defaulted "Kentucky bluegrass" is a
 * guess, not an answer). When the grass IS known, the tool's existing
 * RelatedTools block stays the next-step surface — never both in one slot.
 *
 * Design: variant 1c of the approved next-step-card family
 * (obsidian/research/design-inspiration/next-step-cards.md) — the featured hero
 * treatment reserved for "one next step clearly dominates", which is exactly
 * this case. Eyebrow, context chip, heading, tagline, effort line, forward-motion
 * pill.
 *
 * FORKED MARKUP, SHARED MECHANICS. It does not wrap components/blog/inline-scan-cta
 * because that component is a white glass mid-article banner with no styling seam
 * (no className prop) and it owns its own heading/tagline, which would double up
 * inside a hero panel. The parts that actually matter are reused verbatim:
 * `useScanHandoff` (the iOS-safe photo stash + navigation) and the paired
 * cta_surface_shown / cta_clicked contract on ONE identical `cta_location`.
 *
 * Styling is inline for the same reason as
 * components/tools/fertilizer-schedule/calculator-client.tsx, which is one of the
 * hosts: the site CSS is a precompiled string (lib/generated/tailwind.ts) that
 * does not pick up new arbitrary classes until compile-tailwind runs.
 */

import { Camera, ArrowRight, Loader2 } from "lucide-react"
import { analytics, trackEvent } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import { useScanHandoff, SCAN_ACCEPT } from "@/hooks/use-scan-handoff"
import { IDENTIFY_CTA, identifyCtaProps } from "@/lib/identify-cta"

const CTA_LOCATION = IDENTIFY_CTA.location.toolResultIdentify

interface ToolNextStepCardProps {
  /** Tool this card is rendered on. Rides on both analytics events as `tool_id`. */
  toolId: string
  heading: string
  tagline: string
  /**
   * The grass the tool silently assumed, e.g. "Kentucky bluegrass". Shown as the
   * context chip so the assumption is visible instead of buried in the math.
   */
  assumedGrassLabel?: string
  className?: string
}

export function ToolNextStepCard({
  toolId,
  heading,
  tagline,
  assumedGrassLabel,
  className,
}: ToolNextStepCardProps) {
  const { inputRef, handingOff, openPicker, onInputChange } = useScanHandoff(CTA_LOCATION)
  const impressionRef = useImpressionEvent<HTMLButtonElement>(
    CTA_LOCATION,
    { surface: "tool_next_step_card", tool_id: toolId },
    { emit: analytics.ctaSurfaceShown },
  )

  return (
    <section className={className} style={{ marginTop: 24 }}>
      <p
        style={{
          margin: "0 0 10px",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: "#16a34a",
        }}
      >
        What to do next
      </p>
      <button
        ref={impressionRef}
        type="button"
        onClick={() => {
          trackEvent("cta_clicked", {
            ...identifyCtaProps(CTA_LOCATION),
            tool_id: toolId,
          })
          openPicker()
        }}
        disabled={handingOff}
        aria-label="Upload a photo of your grass to identify it"
        style={{
          display: "block",
          width: "100%",
          boxSizing: "border-box",
          padding: 28,
          borderRadius: 24,
          border: "none",
          textAlign: "left",
          fontFamily: "inherit",
          cursor: handingOff ? "default" : "pointer",
          color: "#fff",
          background: "linear-gradient(90deg, #16a34a 0%, #059669 100%)",
          boxShadow: "0 18px 44px -18px rgba(22,163,74,0.55)",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            padding: "4px 12px",
            borderRadius: 9999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: "#fff",
            background: "rgba(255,255,255,0.2)",
          }}
        >
          <Camera style={{ width: 13, height: 13 }} aria-hidden="true" />
          {assumedGrassLabel ? `Assuming ${assumedGrassLabel}` : "Recommended first"}
        </span>
        <span
          style={{
            display: "block",
            marginTop: 14,
            fontSize: 24,
            fontWeight: 700,
            lineHeight: 1.25,
            letterSpacing: "-0.025em",
            color: "#fff",
          }}
        >
          {heading}
        </span>
        <span
          style={{
            display: "block",
            marginTop: 8,
            fontSize: 14,
            lineHeight: 1.55,
            color: "rgba(255,255,255,0.85)",
          }}
        >
          {tagline}
        </span>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 14,
            flexWrap: "wrap",
            marginTop: 24,
          }}
        >
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.75)" }}>
            Takes about 10 seconds
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              minHeight: 44,
              padding: "0 20px",
              borderRadius: 9999,
              fontSize: 14,
              fontWeight: 700,
              color: "#16a34a",
              background: "#fff",
            }}
          >
            {handingOff ? (
              <>
                <Loader2
                  style={{ width: 15, height: 15 }}
                  className="animate-spin"
                  aria-hidden="true"
                />
                Starting your ID
              </>
            ) : (
              <>
                Snap or upload a photo
                <ArrowRight style={{ width: 15, height: 15 }} aria-hidden="true" />
              </>
            )}
          </span>
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={SCAN_ACCEPT}
        capture="environment"
        style={{ display: "none" }}
        onChange={onInputChange}
      />
    </section>
  )
}
